import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Form,
  Spinner,
  Container,
  Button,
} from "react-bootstrap";
import { AddPatientSteps } from "../PatientsAccounts/AddPatientInfo";
import {
  fetchPractitionersByPractices,
  fetchUserAppointmentsByDate,
  getServiceById,
} from "../../api";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { StatusPopover } from "./UpdateStatus";
import "./ListView.css";
import "../../custom.scss";
import CreateReceiptModal from "../Accounts/Receipt";

export default function ListView() {
  const [practitionersByDate, setPractitionersByDate] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [userId, setUserId] = useState(null);
  const [practices, setPractices] = useState([]);
  const [serviceCache, setServiceCache] = useState({});
  const [showPastEventsMap, setShowPastEventsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalShow, setModalShow] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [expandedPractitioners, setExpandedPractitioners] = useState({});
  const [receiptModalShow, setReceiptModalShow] = useState(false);
  const [patient, setPatient] = useState(null);
  const [patientId, setPatientId] = useState(null);
  const [show, setShow] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showPatient, setShowPatient] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const navigate = useNavigate();

  const formatToYYYYMMDD = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const token = sessionStorage.getItem("user");
    if (!token) {
      console.error("No user token found in session storage.");
      return;
    }
    const decoded = jwtDecode(token);
    setUserId(decoded._id);
    setPractices(decoded.practice || []);
  }, []);

  useEffect(() => {
    const fetchPracticesAndAppointments = async () => {
      if (!userId) return;

      setLoading(true);

      try {
        const practitioners = await fetchPractitionersByPractices(practices);
        setPractices(practitioners);

        const today = formatToYYYYMMDD(new Date());

        const appointmentsResponse = await fetchUserAppointmentsByDate(
          userId,
          today
        );
        setAppointments(appointmentsResponse);

        setPractitionersByDate({ [today]: practitioners });

        const uncachedServiceIds = [
          ...new Set(appointmentsResponse.map((appt) => appt.serviceId)),
        ].filter((serviceId) => !serviceCache[serviceId]);

        const newServiceCache = {};
        for (const serviceId of uncachedServiceIds) {
          try {
            const service = await getServiceById(serviceId);
            newServiceCache[serviceId] = service.servicesName;
          } catch {
            newServiceCache[serviceId] = "Unknown Service";
          }
        }
        setServiceCache((prevCache) => ({
          ...prevCache,
          ...newServiceCache,
        }));

        setShowPastEventsMap((prevMap) => {
          const newMap = { ...prevMap };
          practitioners.forEach((practitioner) => {
            if (!(practitioner.id in newMap)) {
              newMap[practitioner.id] = false;
            }
          });
          return newMap;
        });

        const initialExpandedState = {};
        practitioners.forEach((practitioner) => {
          initialExpandedState[practitioner.id] = true;
        });
        setExpandedPractitioners(initialExpandedState);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPracticesAndAppointments();
    console.log("Appointments fetched:", appointments);
  }, [userId, refreshTick]);

  const togglePractitioner = (id) => {
    setExpandedPractitioners((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const formatDate = (date) => {
    const options = { day: "2-digit", month: "short", year: "numeric" };
    return new Date(date).toLocaleDateString(undefined, options);
  };

  const sortAppointmentsByStartTime = (appointments) => {
    return [...appointments].sort(
      (a, b) => new Date(a.start) - new Date(b.start)
    );
  };

  const renderStatusIcon = (status, appointmentId, appointment) => {
    if (!appointment) return null;

    return (
      <StatusPopover
        appointmentId={appointmentId}
        patientId={appointment.patientId}
        currentStatus={status}
        onUpdate={(newStatus) =>
          handleStatusUpdate(appointmentId, newStatus, appointment.patientId)
        }
      />
    );
  };

  const handleStatusUpdate = (appointmentId, newStatus, patientId) => {
    setAppointments((prevAppointments) =>
      prevAppointments.map((appt) =>
        appt._id === appointmentId ? { ...appt, status: newStatus } : appt
      )
    );

    if (newStatus === "Completed") {
      // ✅ Navigate to saved notes after completion (optional)
      navigate(`/patients/appointment/${patientId}/1`);
    }
  };

  const renderTable = (practitioner, date) => {
    const currentTime = new Date();
    const twoHoursAgo = new Date(currentTime.getTime() - 2 * 60 * 60 * 1000);

    const showPastEvents = showPastEventsMap[practitioner.id] || false;

    const pastAppointments = appointments.filter(
      (appt) =>
        appt.resourceId === practitioner.id &&
        appt.start.startsWith(date) &&
        new Date(appt.end) < twoHoursAgo
    );

    const currentAppointments = appointments.filter(
      (appt) =>
        appt.resourceId === practitioner.id &&
        appt.start.startsWith(date) &&
        new Date(appt.end) >= twoHoursAgo
    );

    const sortedCurrentAppointments =
      sortAppointmentsByStartTime(currentAppointments);
    const sortedPastAppointments =
      sortAppointmentsByStartTime(pastAppointments);

    const handleDescriptionCellClick = (appointment) => {
      if (appointment.status === "New Patient") {
        setModalData({
          firstName: appointment.patientFirstName || "Unknown",
          lastName: appointment.patientLastName || "Unknown",
          email: appointment.patientEmail || "Unknown",
          appointmentId: appointment._id,
        });
        setModalShow(true);
        return;
      }

      if (appointment.status === "Cancelled") {
        // optionally: show a toast/tooltip here
        return; // 🚫 block navigation
      }
      if (appointment.status === "Completed") {
        navigate(
          `/patients/appointment/${appointment.patientId}/1`
        );
      } else {
        navigate(
          `/patients/appointment/${appointment.patientId}/${appointment._id}`
        );
      }

    };

    return (
      <div className="mb-0">
        {pastAppointments.length > 0 && (
          <Form.Check
            className="d-flex justify-content-start mb-2"
            type="switch"
            id={`toggle-past-events-${practitioner.id}`}
            label={
              <span style={{ marginLeft: "10px" }}>
                {showPastEvents ? "Hide Earlier" : "Earlier"}
              </span>
            }
            checked={showPastEvents}
            onChange={() =>
              setShowPastEventsMap((prev) => ({
                ...prev,
                [practitioner.id]: !prev[practitioner.id],
              }))
            }
          />
        )}
        {showPastEvents && pastAppointments.length > 0 && (
          <Table className="ms-1 me-1">
            <tbody>
              {sortedPastAppointments.map((appointment) => {
                const isNewPatient = appointment.status === "New Patient";
                const isCancelled = appointment.status === "Cancelled";
                const isBlocked = isCancelled;

                const timeRange = `${new Date(
                  appointment.start
                ).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })} - ${new Date(appointment.end).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}`;

                return (
                  <tr key={appointment._id}>
                    <td style={{ width: "15%" }}>{timeRange}</td>

                    <td
                      style={{ width: "5%" }}
                      className="display-flex align-items-center justify-content-center"
                    >
                      {renderStatusIcon(
                        appointment.status,
                        appointment._id,
                        appointment
                      )}
                    </td>

                    <td
                      onClick={
                        !isBlocked
                          ? () => handleDescriptionCellClick(appointment)
                          : undefined
                      }
                      style={{
                        cursor: isBlocked ? "default" : "pointer", // 👈 no hand cursor when blocked
                        width: "80%",
                        opacity: isCancelled ? 0.6 : 1,
                      }}
                      title={
                        isNewPatient
                          ? "Complete patient details first"
                          : isCancelled
                          ? "Cancelled appointment — cannot open"
                          : ""
                      }
                      className={isBlocked ? "text-muted" : ""}
                    >
                      {`${appointment.title} - ${
                        serviceCache[appointment.serviceId] || "Loading..."
                      }`}
                    </td>

                    <td style={{ width: "5%" }} className="text-end">
                      {appointment.status === "Completed" && ( // 👈 only for Completed
                        <Button
                          variant="outline-primary"
                          className="d-flex align-items-center justify-content-center gap-2"
                          onClick={() => setReceiptModalShow(true)}
                          title="Open receipt"
                        >
                          <i className="fa-solid fa-receipt text-primary"></i>
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        <Table className="ms-1 me-1">
          <tbody>
            {sortedCurrentAppointments.map((appointment) => {
              const isNewPatient = appointment.status === "New Patient";
              const isCancelled = appointment.status === "Cancelled";
              const isBlocked = isCancelled;

              const timeRange = `${new Date(
                appointment.start
              ).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })} - ${new Date(appointment.end).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}`;

              return (
                <tr key={appointment._id}>
                  <td style={{ width: "15%" }}>{timeRange}</td>

                  <td style={{ width: "5%" }}>
                    {renderStatusIcon(
                      appointment.status,
                      appointment._id,
                      appointment
                    )}
                  </td>

                  <td
                    onClick={
                      !isBlocked
                        ? () => handleDescriptionCellClick(appointment)
                        : undefined
                    }
                    style={{
                      cursor: isBlocked ? "default" : "pointer", // 👈 no hand cursor when blocked
                      width: "80%",
                      opacity: isCancelled ? 0.6 : 1,
                    }}
                    title={
                      isNewPatient
                        ? "Complete patient details first"
                        : isCancelled
                        ? "Cancelled appointment — cannot open"
                        : ""
                    }
                    className={isBlocked ? "text-muted" : ""}
                  >
                    {`${appointment.title} - ${
                      serviceCache[appointment.serviceId] || "Loading..."
                    }`}
                  </td>

                  <td style={{ width: "5%" }} className="text-end">
                    {appointment.status === "Completed" && ( // 👈 only for Completed
                      <Button
                        variant=""
                        size="lg"
                        className="d-flex rounded-0 align-items-center justify-content-center gap-2"
                        onClick={() => {
                          setReceiptModalShow(true);
                          setPatientId(appointment.patientId); // Set patient ID for receipt
                        }}
                        title="Open receipt"
                      >
                        <i className="fa-solid fa-receipt text-primary"></i>
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  const today = new Date().toISOString().split("T")[0];

  const renderAccordion = useMemo(() => {
    const today = Object.keys(practitionersByDate)[0];
    const practitioners = practitionersByDate[today] || [];

    return (
      <Container>
        {practitioners.length > 0 ? (
          practitioners.map((practitioner) => {
            const isExpanded = expandedPractitioners[practitioner.id];
            return (
              <div key={practitioner.id} className="mb-3 w-100">
                <div
                  className={`d-flex justify-content-between align-items-center px-3 py-2 ${
                    isExpanded ? "bgheader" : "bg-light text-primary"
                  }`}
                  style={{ borderBottom: "1px solid #ccc" }}
                >
                  <span>{practitioner.title}</span>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => togglePractitioner(practitioner.id)}
                    className={`p-0 ${
                      isExpanded ? "bgheader" : "text-primary"
                    }`}
                  >
                    {isExpanded ? (
                      <i className="fa-solid fa-angle-up"></i>
                    ) : (
                      <i className="fa-solid fa-angle-down"></i>
                    )}
                  </Button>
                </div>
                {isExpanded && (
                  <Container className="mt-2 ms-2 table-responsive">
                    {renderTable(practitioner, today)}
                  </Container>
                )}
              </div>
            );
          })
        ) : (
          // Show message when no practitioners are found
          <div className="text-center text-secondary mt-3">
            <h6 className="fw-light">No appointments scheduled for today</h6>
          </div>
        )}
      </Container>
    );
  }, [
    practitionersByDate,
    expandedPractitioners,
    serviceCache,
    appointments,
    showPastEventsMap,
  ]);

  return (
    <div>

      {loading ? (
        <Spinner animation="border" />
      ) : (
        <>
          {renderAccordion}
          {modalShow && (
            <AddPatientSteps
              show={modalShow}
              onHide={() => setModalShow(false)}
              patientFirstName={modalData?.firstName}
              patientLastName={modalData?.lastName}
              patientEmail={modalData?.email}
              appointmentId={modalData?.appointmentId}
              onSaved={() => {
                setModalShow(false);
                setRefreshTick((t) => t + 1); // 🔁 triggers re-fetch
              }}
            />
          )}
        </>
      )}
      {receiptModalShow && (
        <CreateReceiptModal
          show={receiptModalShow}
          onHide={() => setReceiptModalShow(false)}
          patientId={patientId}
        />
      )}
    </div>
  );
}
